import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type RefObject,
} from "react";
import { imageSrc } from "@shared/vod-images";
import { getBinding, getScreenId } from "../../../lib/binding";
import { playbackZapped } from "../../../lib/playbackTelemetry";
import { resolveItemPlayback } from "../../../signage/playbackLicence";
import {
  useTvLineup,
  type TvLineup,
  type TvLineupChannel,
} from "../../../hooks/useTvLineup";
import { RemoteHint, type RemoteHintItem } from "../RemoteHint";
import { useScheme } from "../schemeContext";
import { useFocusGrid } from "../useFocusGrid";
import { useNow } from "../useNow";
import { usePlayReport } from "../player";
import StreamSurface from "../StreamSurface";
import { STREAM_FAILURE_MESSAGE, type StreamFailure } from "../streamPlayback";
import { ChannelBar, gapMinutes } from "./ChannelBar";
import { guideCopy, type GuideCopy } from "./guideCopy";
import {
  appendDigit,
  eventClock,
  nowNext,
  resolveDigits,
  stepChannel,
  tunable,
  type NowNext,
} from "./lineup";
import {
  initialZap,
  mountedSurfaces,
  PREWARM_AFTER_MS,
  shownChannel,
  TUNE_SETTLE_MS,
  TUNE_TIMEOUT_MS,
  tuning,
  zapReducer,
} from "./zap";
import { useZapKeys, type ZapMode } from "./useZapKeys";

/**
 * The guide screen: live television, zapping, now/next (`TV-4`).
 *
 * **Today "TV" is a hand-off, not a feature** — pressing it launches
 * `com.noriginmedia.nenda.nm`, and that launch IS the boot time this whole
 * series exists to remove. This screen is the replacement: our own player, our
 * own line-up, our own guide, with a premise-level switch deciding which of the
 * two a room gets.
 *
 * **One renderer, every template.** A template supplies a {@link
 * GuideScreenStyle} of tokens and nothing else — the `CustomScreen` /
 * `OffersScreen` precedent. Nine bespoke guides would be nine keyboard models to
 * debug on a real remote and nine places the next failure sentence has to be
 * added.
 *
 * ## The three things this screen is judged on
 *
 * 1. **Zap latency.** See `zap.ts`: one pre-warmed spare, a settle window, a
 *    poster over the gap, and every change TIMED and reported
 *    (`playbackZapped`). Targets are ≤ 1200 ms onto the pre-warmed neighbour and
 *    ≤ 2500 ms cold, keypress → first frame. ⚠️ Not yet measured on hardware; if
 *    the fleet says otherwise, live TV legitimately stays on the vendor app
 *    while VOD moves.
 * 2. **A channel with no EPG says why.** An unmapped channel is silent today
 *    (`BE-22`): upstream answers `200 []` and the row renders blank. `gaps[]`
 *    exists for precisely this, and {@link nowNext} turns it into one of three
 *    stated conditions — live, a hole, or no guide source at all.
 * 3. **A failure is named.** Playback failures come from `StreamSurface`'s typed
 *    union with a sentence already chosen; a load that never starts is called
 *    out after {@link TUNE_TIMEOUT_MS} rather than spinning forever.
 *
 * ## The decoder ceiling
 *
 * At most TWO video surfaces exist at any instant — the live one and at most one
 * pre-warmed spare — enforced by `mountedSurfaces()`, which is the only thing
 * this file renders players from. A hotel panel's decoders are pooled in the
 * single digits and an Android WebView does not release one when a `<video>`
 * leaves the DOM ([[panel-video-encoding]]); signage settled on "two layers,
 * never more" and this is the same deal.
 */

export interface GuideScreenStyle {
  /** Page ground behind the list, and the letterbox behind the picture. */
  bg: string;
  /** Row / panel surface. */
  card: string;
  /** Primary text. */
  ink: string;
  /** Secondary text — synopses, times, meta lines. */
  inkDim: string;
  /** Hairline between rows, and the unfilled part of the progress bar. */
  rule: string;
  /** Focus ring, channel number, progress fill. */
  accent: string;
  /** Text colour ON the accent fill. */
  onAccent: string;
  /** Display font for titles. */
  titleFont: string;
  /** Body font. */
  font: string;
  /** Corner radius the template uses on its own cards. */
  radius: number;
  /** Tone behind a channel with no artwork. */
  placeholder: string;
}

interface Props {
  style: GuideScreenStyle;
  /** Leaves the screen — the Back key while watching. */
  onBack: () => void;
  scopeRef: RefObject<HTMLElement>;
  /** What the premise calls this screen. Blank falls back to the copy pack. */
  title?: string;
  /**
   * Inner padding. A host that owns the whole viewport passes the TV-safe
   * constants (the default); a host that has already inset its sub-pages passes
   * zeroes, or the page is indented twice.
   */
  pad?: { x: number; y: number };
  /** Draw the remote-hint strip along the foot. */
  hints?: boolean;
  /**
   * A line-up for the design preview, which has no pairing and no backend.
   * A bound television never receives one.
   */
  preview?: TvLineup;
  /**
   * Which channel to land on, when the guest arrived by choosing one.
   *
   * Absent means "whatever the line-up starts with", which is right for a
   * screen opened cold from a tile. It is wrong when the home carousel sent the
   * guest here: they pressed OK on a specific channel, and landing on the first
   * channel in the line-up instead would look like the remote misfired.
   *
   * Honoured only for the LANDING. Once the guest zaps, this is not consulted
   * again - re-applying it would drag them back every time the line-up
   * refreshed. An id no longer in the line-up falls through to the same
   * first-channel default as an absent one, because a channel that has been
   * withdrawn is exactly the case the landing effect already handles.
   */
  startChannelId?: string;
}

/** The TV-safe area, same constants as every other shared screen. */
export const SAFE_X = 96;
export const SAFE_Y = 72;

/** How long the channel bar stays up after a change. */
const BAR_MS = 5_000;
/** How long channel-number entry waits for another digit. */
const DIGIT_SETTLE_MS = 1_200;
/** How long "no channel 47" stays on screen. */
const MISS_MS = 2_000;
/** Rows of the channel list on screen at once. */
const LIST_ROWS = 7;

export default function GuideScreen({
  style,
  onBack,
  scopeRef,
  title,
  pad = { x: SAFE_X, y: SAFE_Y },
  hints = true,
  preview,
  startChannelId,
}: Props) {
  const scheme = useScheme();
  const copy = guideCopy(scheme.context.locale);
  // The router already holds this feed (it is what decided the tile exists),
  // so take it rather than asking again. Falling through to the hook keeps a
  // host that mounts this screen on its own working, and `preview` keeps the
  // design branch working - all three are the same thing to the hook: a
  // line-up somebody already has.
  const lineup = useTvLineup(scheme.guide ?? preview);
  const channels = useMemo(() => lineup?.channels ?? [], [lineup]);
  const now = useNow();

  const [zap, dispatch] = useReducer(zapReducer, undefined, initialZap);
  const [mode, setMode] = useState<ZapMode>("watch");
  const [barToken, setBarToken] = useState(0);
  const [barVisible, setBarVisible] = useState(true);
  const [digits, setDigits] = useState("");
  const [miss, setMiss] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** The last measured change, keypress → first frame. Shown to nobody; kept
   *  so a probe build can print it beside the tally. */
  const [lastZapMs, setLastZapMs] = useState<number | null>(null);
  /** The change being timed: which channel, and when the key went down. */
  const zapStart = useRef<{ id: string; at: number } | null>(null);
  /** Which way the guest is travelling — decides which neighbour gets warmed. */
  const direction = useRef(1);

  const byId = useMemo(
    () => new Map(channels.map((c) => [c.id, c])),
    [channels]
  );

  /* -- landing, and surviving a line-up that changed under us -------------- */

  useEffect(() => {
    if (channels.length === 0) return;
    // A channel that leaves the line-up cannot stay on screen: the manifest
    // behind it is gone, and a player left pointing at it would fail with a
    // name that describes the symptom rather than the cause.
    if (!zap.live || !byId.has(zap.live)) {
      // `startChannelId` is the channel the guest actually chose, and it only
      // gets a say here - on the landing. A withdrawn id falls through to the
      // first channel, which is the same answer this effect already gives when
      // a live channel leaves the line-up.
      const wanted =
        startChannelId && byId.has(startChannelId)
          ? startChannelId
          : channels[0].id;
      dispatch({ type: "land", id: wanted });
    }
  }, [channels, byId, zap.live, startChannelId]);

  /* -- aiming ------------------------------------------------------------- */

  const aim = useCallback(
    (id: string | null) => {
      if (!id || !byId.has(id)) return;
      setBarToken((n) => n + 1);
      setMiss(null);
      if (id !== zap.live) {
        // The clock starts at the PRESS, not at the commit: the settle window
        // and the licence round trip are both part of what the guest waits for,
        // and a measurement that excluded them would flatter the design.
        //
        // It is stamped with the channel it belongs to, so a change that was
        // cancelled or overtaken can never be attributed to whatever ends up
        // playing. Pressing ch+ and then ch− back onto the live channel is a
        // change that did not happen, and must not appear in `zapMsWorst`.
        zapStart.current = { id, at: Date.now() };
        setNote(null);
      } else {
        zapStart.current = null;
      }
      dispatch({ type: "aim", id });
    },
    [byId, zap.live]
  );

  /** ch+ / ch−, and the direction that decides which neighbour gets warmed. */
  const step = useCallback(
    (delta: number) => {
      if (channels.length === 0) return;
      direction.current = delta >= 0 ? 1 : -1;
      aim(stepChannel(channels, shownChannel(zap), delta));
    },
    [aim, channels, zap]
  );

  /* -- the channel list's focus ------------------------------------------- */

  const [focus, setFocus] = useFocusGrid({
    rows: Math.max(1, channels.length),
    cols: 1,
    scopeRef,
    wrap: true,
    // Exactly one owner per mode: while watching, `useZapKeys` has the arrows.
    enabled: mode === "list",
    onEnter: (f) => {
      const target = channels[f.r];
      if (target) aim(target.id);
      setMode("watch");
    },
    onBack: () => setMode("watch"),
  });

  /* -- the settle window -------------------------------------------------- */

  useEffect(() => {
    if (!zap.pending) return;
    // Onto the pre-warmed spare there is nothing to wait for: the surface is
    // already mounted, so committing at once costs no decoder and no load.
    const delay = zap.pending === zap.spare ? 0 : TUNE_SETTLE_MS;
    const t = setTimeout(() => dispatch({ type: "commit" }), delay);
    return () => clearTimeout(t);
  }, [zap.pending, zap.spare]);

  /* -- the measurement ---------------------------------------------------- */

  useEffect(() => {
    if (!zap.live || !zap.playing.includes(zap.live)) return;
    setNote(null);
    const started = zapStart.current;
    // Only the change this clock was started for. `zap.playing` gets a fresh
    // array identity whenever the spare comes or goes, so this effect runs at
    // moments that have nothing to do with a channel change.
    if (!started || started.id !== zap.live) return;
    zapStart.current = null;
    const ms = Date.now() - started.at;
    setLastZapMs(ms);
    playbackZapped(ms);
  }, [zap.live, zap.playing]);

  /* -- never spin forever ------------------------------------------------- */

  useEffect(() => {
    const live = zap.live;
    if (!live || zap.playing.includes(live)) return;
    const channel = byId.get(live);
    if (channel && !tunable(channel)) {
      // Nothing to load: the line-up carries this channel and this television
      // has no media for it. Say that instead of waiting twelve seconds to say
      // something vaguer.
      setNote(copy.notTunable);
      return;
    }
    const t = setTimeout(() => setNote(copy.tuneTimeout), TUNE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [zap.live, zap.playing, byId, copy]);

  /* -- pre-warming -------------------------------------------------------- */

  useEffect(() => {
    if (!zap.prewarmAllowed || zap.pending || !zap.live) return;
    // Only once the live channel is actually playing. Warming while the guest
    // is still waiting for the picture spends a decoder on the wrong thing.
    if (!zap.playing.includes(zap.live)) return;
    const target = stepChannel(channels, zap.live, direction.current);
    if (!target || target === zap.live) return;
    const channel = byId.get(target);
    if (!channel || !tunable(channel)) return;
    const t = setTimeout(
      () => dispatch({ type: "warm", id: target }),
      PREWARM_AFTER_MS
    );
    return () => clearTimeout(t);
  }, [zap.prewarmAllowed, zap.pending, zap.live, zap.playing, channels, byId]);

  /**
   * Licence pre-fetch for BOTH neighbours, without mounting anything.
   *
   * The spare buys the fast path in one direction; this buys most of it in the
   * other, at the cost of a JSON round trip and no decoder at all. The
   * resolution lands in `playbackLicence`'s own cache, keyed to the token's
   * expiry, so a zap either way skips the play call entirely.
   */
  useEffect(() => {
    if (!zap.live || zap.pending) return;
    const binding = getBinding();
    if (!binding) return;
    const screenId = getScreenId();
    const neighbours = [1, -1]
      .map((d) => stepChannel(channels, zap.live, d))
      .map((id) => (id ? byId.get(id) : undefined))
      .filter((c) => c && c.contentAssetId && !c.url);
    let alive = true;
    const t = setTimeout(() => {
      if (!alive) return;
      for (const channel of neighbours) {
        void resolveItemPlayback({
          apiBase: "",
          caller: { kind: "screen", screenId, token: binding.screenToken },
          contentAssetId: channel!.contentAssetId!,
        });
      }
    }, PREWARM_AFTER_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [zap.live, zap.pending, channels, byId]);

  /* -- the bar ------------------------------------------------------------ */

  useEffect(() => {
    setBarVisible(true);
    const t = setTimeout(() => setBarVisible(false), BAR_MS);
    return () => clearTimeout(t);
  }, [barToken]);

  /* -- channel-number entry ----------------------------------------------- */

  const pushDigit = useCallback(
    (digit: string) => {
      if (channels.length === 0) return;
      setMode("watch");
      setDigits((buffer) => appendDigit(channels, buffer, digit));
    },
    [channels]
  );

  useEffect(() => {
    if (!digits) return;
    const { id, canExtend } = resolveDigits(channels, digits);
    const settle = () => {
      if (id) aim(id);
      else setMiss(copy.noSuchChannel(digits));
      setDigits("");
    };
    // A run that can only mean one channel tunes at once. Waiting 1.2 s to act
    // on "5" when the line-up has no channel 50-something is what makes
    // single-digit zapping feel broken.
    if (id && !canExtend) {
      settle();
      return;
    }
    const t = setTimeout(settle, DIGIT_SETTLE_MS);
    return () => clearTimeout(t);
  }, [digits, channels, aim, copy]);

  useEffect(() => {
    if (!miss) return;
    const t = setTimeout(() => setMiss(null), MISS_MS);
    return () => clearTimeout(t);
  }, [miss]);

  /* -- keys --------------------------------------------------------------- */

  useZapKeys({
    scopeRef,
    mode,
    onStep: step,
    onDigit: pushDigit,
    onLast: () => aim(zap.prev),
    onInfo: () => setBarToken((n) => n + 1),
    onOpen: () => {
      // Open the list ON the channel being watched, not on wherever the focus
      // was left last time.
      const at = channels.findIndex((c) => c.id === zap.live);
      if (at >= 0) setFocus({ r: at, c: 0 });
      setMode("list");
    },
    onDetail: () => setMode("detail"),
    onWatch: () => {
      const target = channels[focus.r];
      if (target) aim(target.id);
      setMode("watch");
    },
    onBack: () => (mode === "detail" ? setMode("list") : onBack()),
  });

  /* -- what is on ---------------------------------------------------------- */

  const shownId = shownChannel(zap);
  const shown = shownId ? byId.get(shownId) : undefined;
  const shownInfo = useMemo(
    () => (shown ? nowNext(shown, now) : null),
    [shown, now]
  );
  const focused = channels[focus.r];
  const focusedInfo = useMemo(
    () => (focused ? nowNext(focused, now) : null),
    [focused, now]
  );

  const surfaces = mountedSurfaces(zap);
  const isTuning = tuning(zap);

  if (!lineup) {
    return (
      <Shell style={style} pad={pad}>
        <div data-testid="guide-note" style={{ fontSize: 34 }}>
          {copy.loading}
        </div>
      </Shell>
    );
  }
  if (channels.length === 0) {
    return (
      <Shell style={style} pad={pad}>
        <div data-testid="guide-note" style={{ fontSize: 34 }}>
          {copy.empty}
        </div>
      </Shell>
    );
  }

  return (
    <div
      data-testid="guide-screen"
      data-mode={mode}
      data-surfaces={surfaces.length}
      data-zap-ms={lastZapMs ?? ""}
      style={{
        position: "absolute",
        inset: 0,
        background: style.bg,
        color: style.ink,
        fontFamily: style.font,
        overflow: "hidden",
      }}
    >
      {/* At most two, ever. `mountedSurfaces` is the ceiling. */}
      {surfaces.map((id) => {
        const channel = byId.get(id);
        if (!channel) return null;
        const live = id === zap.live;
        return (
          <div
            key={id}
            data-testid={`guide-surface-${id}`}
            data-live={live ? "true" : "false"}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: live ? 1 : 0,
              opacity: live ? 1 : 0,
            }}
          >
            <ChannelSurface
              channel={channel}
              live={live}
              muted={!live}
              onPlaying={() => dispatch({ type: "playing", id })}
              onFailed={(failure) => {
                dispatch({ type: "failed", id, failure });
                // The player classified this instantly and already chose a
                // sentence for it. Saying it NOW is the difference between a
                // guest who knows to try another channel and one who watches
                // "Changing channel…" for twelve seconds and then gets a
                // vaguer line. The poster is on top of the surface's own
                // overlay, so the poster is what has to carry it.
                //
                // 🚩 `STREAM_FAILURE_MESSAGE` is English-only (it is shipped
                // that way by PLAYER-9), so a Swedish premise reads one English
                // sentence here while the rest of the screen is in its own
                // language. Localising it means localising the shipped table,
                // which is not this unit's file.
                if (id !== zap.live) return;
                zapStart.current = null;
                setNote(STREAM_FAILURE_MESSAGE[failure]);
              }}
            />
          </div>
        );
      })}

      {/* The poster over the gap. Never a spinner, never black. */}
      {shown && (isTuning || note) ? (
        <Poster
          style={style}
          channel={shown}
          info={shownInfo}
          label={note ?? copy.tuning}
          failed={!!note}
        />
      ) : null}

      {mode === "watch" && shown && shownInfo ? (
        <ChannelBar
          style={style}
          copy={copy}
          channel={shown}
          info={shownInfo}
          now={now}
          pad={pad}
          visible={barVisible || isTuning || !!note}
          note={note}
        />
      ) : null}

      {digits || miss ? (
        <div
          data-testid="guide-digits"
          style={{
            position: "absolute",
            top: pad.y,
            right: pad.x,
            zIndex: 4,
            padding: "18px 30px",
            borderRadius: style.radius,
            background: miss ? style.card : style.accent,
            color: miss ? style.ink : style.onAccent,
            fontFamily: style.titleFont,
            fontSize: miss ? 30 : 64,
            lineHeight: 1.2,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {miss ?? digits}
        </div>
      ) : null}

      {mode !== "watch" ? (
        <div
          data-testid="guide-list"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            background: "rgba(0,0,0,0.72)",
            padding: `${pad.y}px ${pad.x}px`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              fontFamily: style.titleFont,
              fontSize: 46,
              lineHeight: 1.2,
              marginBottom: 24,
            }}
          >
            {title?.trim() || copy.title}
            <span style={{ color: style.inkDim, marginLeft: 20, fontSize: 30 }}>
              {copy.listTitle}
            </span>
          </div>
          <div style={{ display: "flex", gap: 40, flex: 1, minHeight: 0 }}>
            <ChannelList
              style={style}
              copy={copy}
              channels={channels}
              focusIndex={focus.r}
              liveId={zap.live}
              now={now}
            />
            <ProgrammePane
              style={style}
              copy={copy}
              info={focusedInfo}
              now={now}
              detail={mode === "detail"}
            />
          </div>
        </div>
      ) : null}

      {hints ? (
        <div
          style={{
            position: "absolute",
            left: pad.x,
            bottom: Math.max(24, pad.y - 40),
            zIndex: 5,
            color: style.inkDim,
          }}
        >
          <RemoteHint items={hintItems(mode, copy)} />
        </div>
      ) : null}
    </div>
  );
}

function hintItems(
  mode: ZapMode,
  copy: ReturnType<typeof guideCopy>
): RemoteHintItem[] {
  if (mode === "watch") {
    return [
      { key: "▲▼", label: copy.hint.zap },
      { key: "0-9", label: copy.hint.digits },
      { key: "◄", label: copy.hint.last },
      { key: "OK", label: copy.hint.guide },
      { key: "←", label: copy.hint.back },
    ];
  }
  if (mode === "list") {
    return [
      { key: "▲▼", label: copy.hint.zap },
      { key: "OK", label: copy.hint.watch },
      { key: "►", label: copy.hint.detail },
      { key: "←", label: copy.hint.back },
    ];
  }
  return [
    { key: "OK", label: copy.hint.watch },
    { key: "←", label: copy.hint.back },
  ];
}

/** The empty / loading frame, so those two states are not a blank rectangle. */
function Shell({
  style,
  pad,
  children,
}: {
  style: GuideScreenStyle;
  pad: { x: number; y: number };
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="guide-screen"
      style={{
        position: "absolute",
        inset: 0,
        background: style.bg,
        color: style.inkDim,
        fontFamily: style.font,
        padding: `${pad.y}px ${pad.x}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The picture                                                                */
/* -------------------------------------------------------------------------- */

/**
 * One channel's player.
 *
 * Everything about DRM, decoder release and error classification belongs to
 * `StreamSurface` (`PLAYER-9`) and none of it is repeated here. This component's
 * only job is turning a line-up row into the two things that surface wants: a
 * manifest and a way to mint a licence.
 *
 * The licence is minted through the shipped play endpoint rather than carried in
 * the guide payload, for the reason `playbackLicence.ts` states: a Verimatrix
 * token lives five minutes and the guide is cached for ten, so a licence in the
 * guide payload would be stale before it was read. `mintLicence` is called on
 * EVERY licence request the CDM makes, which is what removes renewal as a
 * concept for a channel that plays all evening.
 */
function ChannelSurface({
  channel,
  live,
  muted,
  onPlaying,
  onFailed,
}: {
  channel: {
    id: string;
    title?: string;
    url?: string;
    contentAssetId?: string;
  };
  /** This surface is the one on screen, not the pre-warmed spare. */
  live: boolean;
  muted: boolean;
  onPlaying: () => void;
  onFailed: (failure: StreamFailure) => void;
}) {
  const [url, setUrl] = useState<string | null>(channel.url ?? null);
  const [licence, setLicence] = useState<{ widevineUrl: string | null } | null>(
    null
  );
  const failedRef = useRef(onFailed);
  useEffect(() => {
    failedRef.current = onFailed;
  });

  const assetId = channel.contentAssetId;
  const ownUrl = channel.url;

  useEffect(() => {
    if (ownUrl) {
      setUrl(ownUrl);
      setLicence(null);
      return;
    }
    const binding = getBinding();
    if (!assetId || !binding) return;
    let alive = true;
    void resolveItemPlayback({
      apiBase: "",
      caller: {
        kind: "screen",
        screenId: getScreenId(),
        token: binding.screenToken,
      },
      contentAssetId: assetId,
    }).then((resolved) => {
      if (!alive) return;
      const playback = resolved.playback;
      if (!playback) {
        // The play endpoint said no. That is an entitlement or catalogue fact
        // with a sentence already written for it - far better than a picture
        // that never arrives and never explains itself.
        failedRef.current("licence_refused");
        return;
      }
      setUrl(playback.url);
      setLicence(
        playback.licenceUrl ? { widevineUrl: playback.licenceUrl } : null
      );
    });
    return () => {
      alive = false;
    };
  }, [assetId, ownUrl]);

  const mintLicence = useCallback(async () => {
    const binding = getBinding();
    if (!assetId || !binding) return null;
    const resolved = await resolveItemPlayback({
      apiBase: "",
      caller: {
        kind: "screen",
        screenId: getScreenId(),
        token: binding.screenToken,
      },
      contentAssetId: assetId,
    });
    return resolved.playback?.licenceUrl
      ? { widevineUrl: resolved.playback.licenceUrl }
      : null;
  }, [assetId]);

  /**
   * What this room actually watched (`ANALYTICS-2`).
   *
   * Two conditions, and both are the point:
   *
   * - **`live`.** A pre-warmed spare is mounted, playing and muted behind the
   *   picture so a zap does not pay a player construction. Nobody is watching
   *   it. Passing `null` while it is a spare ends any open session and starts
   *   none, so the session follows the channel the guest is on rather than the
   *   element that happens to be decoding.
   * - **`contentAssetId`.** A channel authored with its own `url` has no
   *   catalogue id, and an id that cannot be joined back to the Content API is
   *   an id we cannot quote to the owner of the content. Nothing is better than
   *   a row keyed on something local.
   */
  const play = usePlayReport(
    live && assetId ? { kind: "channel", assetId, label: channel.title } : null
  );

  // No url yet is not a black rectangle: the poster is over this, and the tune
  // timeout is what decides when "not yet" has become a fault.
  if (!url) return null;

  return (
    <StreamSurface
      url={url}
      licence={licence}
      mintLicence={mintLicence}
      muted={muted}
      onPlaying={onPlaying}
      onProgress={play.onProgress}
      onFailed={(failure) => onFailed(failure)}
    />
  );
}

/** What covers the gap between the keypress and the first frame. */
function Poster({
  style,
  channel,
  info,
  label,
  failed,
}: {
  style: GuideScreenStyle;
  channel: { number: number; title: string };
  info: NowNext | null;
  label: string;
  failed: boolean;
}) {
  const art = info?.current?.image
    ? imageSrc(info.current.image, {
        width: 1280,
        height: 720,
      })
    : null;
  return (
    <div
      data-testid="guide-poster"
      data-failed={failed ? "true" : "false"}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 2,
        background: style.placeholder,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {art ? (
        <img
          src={art}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.4,
          }}
        />
      ) : null}
      <div style={{ position: "relative", textAlign: "center" }}>
        <div
          style={{
            fontFamily: style.titleFont,
            fontSize: 64,
            lineHeight: 1.2,
            color: style.ink,
          }}
        >
          {channel.number} · {channel.title}
        </div>
        <div
          data-testid="guide-poster-label"
          style={{
            marginTop: 16,
            fontSize: 30,
            lineHeight: 1.35,
            color: failed ? style.ink : style.inkDim,
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The list                                                                   */
/* -------------------------------------------------------------------------- */

function ChannelList({
  style,
  copy,
  channels,
  focusIndex,
  liveId,
  now,
}: {
  style: GuideScreenStyle;
  copy: GuideCopy;
  channels: TvLineupChannel[];
  focusIndex: number;
  liveId: string | null;
  now: Date;
}) {
  // A window rather than a scrollbar: a television has no scrollbar and a guest
  // has no pointer. The focused row is kept off the edges so there is always a
  // visible neighbour to move onto.
  const first = Math.max(
    0,
    Math.min(focusIndex - 3, Math.max(0, channels.length - LIST_ROWS))
  );
  const visible = channels.slice(first, first + LIST_ROWS);
  return (
    <div style={{ flex: "0 0 720px", minWidth: 0 }}>
      {visible.map((channel, i) => {
        const index = first + i;
        const info = nowNext(channel, now);
        const focused = index === focusIndex;
        return (
          <div
            key={channel.id}
            data-testid={`guide-row-${channel.id}`}
            aria-current={focused ? "true" : undefined}
            data-live={channel.id === liveId ? "true" : undefined}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 24,
              padding: "16px 22px",
              borderRadius: style.radius,
              marginBottom: 8,
              background: focused ? style.card : "transparent",
              border: `2px solid ${focused ? style.accent : "transparent"}`,
            }}
          >
            <span
              style={{
                minWidth: 72,
                fontFamily: style.titleFont,
                fontSize: 34,
                lineHeight: 1.2,
                color: style.accent,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {channel.number}
            </span>
            <span
              style={{
                minWidth: 210,
                fontSize: 30,
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {channel.title || `${copy.channel} ${channel.number}`}
            </span>
            <span
              data-testid={`guide-row-now-${channel.id}`}
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 26,
                lineHeight: 1.3,
                color: info.current ? style.ink : style.inkDim,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {info.current
                ? `${eventClock(info.current).split(" – ")[0]}  ${info.current.title}`
                : info.state === "unmapped"
                  ? copy.noGuide
                  : copy.noProgrammeNow}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The right-hand pane: now/next, or the programme detail behind ►. */
function ProgrammePane({
  style,
  copy,
  info,
  now,
  detail,
}: {
  style: GuideScreenStyle;
  copy: GuideCopy;
  info: NowNext | null;
  now: Date;
  detail: boolean;
}) {
  if (!info) return null;
  const event = info.current ?? info.next;
  const art = event?.image ? imageSrc(event.image, { width: 800 }) : null;
  return (
    <div
      data-testid={detail ? "guide-detail" : "guide-nownext"}
      style={{
        flex: 1,
        minWidth: 0,
        padding: 32,
        borderRadius: style.radius,
        background: style.card,
        border: `1px solid ${style.rule}`,
      }}
    >
      {!info.current ? (
        // The sentence `BE-22` needs. An unmapped channel is `200 []` upstream
        // and renders blank everywhere else in the platform.
        <div data-testid="guide-detail-noguide">
          <div style={{ fontSize: 34, lineHeight: 1.3, marginBottom: 12 }}>
            {info.state === "unmapped" ? copy.noGuide : copy.noProgrammeNow}
          </div>
          <div style={{ fontSize: 26, lineHeight: 1.4, color: style.inkDim }}>
            {info.state === "unmapped"
              ? copy.noGuideWhy
              : copy.gapFor(gapMinutes(info, now))}
          </div>
        </div>
      ) : (
        <>
          {detail && art ? (
            <img
              src={art}
              alt=""
              style={{
                width: "100%",
                maxHeight: 320,
                objectFit: "cover",
                borderRadius: style.radius,
                marginBottom: 24,
              }}
            />
          ) : null}
          <div style={{ fontSize: 24, color: style.accent, marginBottom: 8 }}>
            {copy.now} · {eventClock(info.current)}
          </div>
          <div
            style={{
              fontFamily: style.titleFont,
              fontSize: 40,
              lineHeight: 1.2,
              marginBottom: 16,
            }}
          >
            {info.current.title}
          </div>
          {info.current.synopsis ? (
            <div
              data-testid="guide-detail-synopsis"
              style={{
                fontSize: 26,
                lineHeight: 1.45,
                color: style.inkDim,
                marginBottom: 24,
              }}
            >
              {info.current.synopsis}
            </div>
          ) : null}
        </>
      )}
      <div
        data-testid="guide-nownext-next"
        style={{
          marginTop: 24,
          paddingTop: 20,
          borderTop: `1px solid ${style.rule}`,
          fontSize: 26,
          lineHeight: 1.35,
          color: style.inkDim,
        }}
      >
        {info.next
          ? `${copy.next} · ${eventClock(info.next).split(" – ")[0]}  ${info.next.title}`
          : copy.guideEnds}
      </div>
    </div>
  );
}
